import { Component, OnInit, Inject } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { TreeNode } from 'primeng/api'; // Use PrimeNG TreeNode
import { OrganizationService } from '../../services/organization.service';
import { Organization } from 'src/app/models/organization.model';
import { DataService } from '../../services/data.service';
import { LocalStoreManager } from '../../services/local-store-manager.service';

export interface OrganizationTreeNode extends TreeNode {
  text: string; // Label for display
  value: number; // Unique identifier
  selectable?: boolean; // Whether the node is selectable
  children?: OrganizationTreeNode[]; // Child nodes
}

@Component({
  selector: 'organization-tree',
  templateUrl: './organization.component.html',
  styleUrls: ['./organization.component.scss'],
  standalone: false
})
export class OrganizationComponent implements OnInit {
  organizations: Organization[] = [];
  newOrganization: Organization[] = [];
  treeData: TreeNode[] = []; // Holds the hierarchical data for PrimeNG Tree
  selectedNodes: TreeNode[] = []; // Holds selected nodes for processing
  isAllSelected: boolean = false; // Tracks the "All" checkbox state

  constructor(
    public dataService: DataService,
    private organizationService: OrganizationService,
    public localStorage: LocalStoreManager,
    public dialogRef: MatDialogRef<OrganizationComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any) {
  }

  ngOnInit(): void {
    this.getOrganizationTree();
  }

  getOrganizationTree(): void {
    this.organizationService.getOrganization().subscribe(
      (results) => {
        this.organizations = results;
        this.fillOrganizationTree();
      },
      (error) => {
        console.error(error);
      }
    );
  }

  fillOrganizationTree(): void {
    const treeData: TreeNode[] = [];
    let currentCompany: TreeNode | null = null;
    let currentFactory: TreeNode | null = null;
    let currentDepartment: TreeNode | null = null;

    this.organizations.forEach((org) => {
      switch (org.type) {
        case 1: // Company
          currentCompany = {
            label: `${org.companyNumber} ${org.description}`,
            data: org.id,
            expanded: false, // Company node is initially collapsed
            selectable: org.isAllowed !== 2,
            children: [],
          };
          treeData.push(currentCompany);
          break;

        case 2: // Factory
          if (currentCompany) {
            currentFactory = {
              label: `${org.factoryNumber} ${org.description}`,
              data: org.id,
              expanded: false, // Factory node is collapsed
              selectable: org.isAllowed !== 2,
              children: [],
            };
            currentCompany.children?.push(currentFactory);
          }
          break;

        case 3: // Department
          if (currentFactory) {
            currentDepartment = {
              label: `${org.departmentNumber} ${org.description}`,
              data: org.id,
              expanded: false, // Department node is collapsed
              selectable: org.isAllowed !== 2,
              children: [],
            };
            currentFactory.children?.push(currentDepartment);
          }
          break;

        case 4: // Section
          if (currentDepartment) {
            currentDepartment.children?.push({
              label: `${org.sectionNumber} ${org.description}`,
              data: org.id,
              selectable: org.isAllowed !== 2,
            });
          }
          break;
      }
    });

    this.treeData = treeData; // Set the hierarchical data for PrimeNG Tree
  }


  save(): void {
    this.newOrganization = this.selectedNodes.map((node) => {
      const level = this.getNodeLevel(node, this.treeData); // Calculate the level dynamically
      // Traverse up the tree to populate parent nodes
      const parentData = this.getParentData(node, this.treeData);

      return {
        type: this.getTypeByLevel(level),
        companyNumber: level === 0 ? node.data : parentData.companyNumber,
        factoryNumber: level === 1 ? node.data : parentData.factoryNumber,
        departmentNumber: level === 2 ? node.data : parentData.departmentNumber,
        sectionNumber: level === 3 ? node.data : parentData.sectionNumber,
        description: level < 3 ? null : node.label, // Only set description for sections
        isAllowed: 1,
        id: 0, // ID should always be 0 as per your requirements
        user: null, // Default or placeholder value; update based on your logic
        system: null, // Default or placeholder value; update based on your logic
        showSvHierarchy: false, // Default value; adjust as needed
      };
    });
    this.dataService.editedOrganization = this.newOrganization;
    this.dialogRef.close();
  }

  getParentData(node: TreeNode, currentTree: TreeNode[], parentData: any = { companyNumber: null, factoryNumber: null, departmentNumber: null, sectionNumber: null }): any {
    for (const parentNode of currentTree) {
      if (parentNode === node) {
        return parentData;
      }
      if (parentNode.children) {
        const newParentData = { ...parentData };

        // Populate parent data based on the node type
        const level = this.getNodeLevel(parentNode, this.treeData);
        switch (level) {
          case 0:
            newParentData.companyNumber = parentNode.data;
            break;
          case 1:
            newParentData.factoryNumber = parentNode.data;
            break;
          case 2:
            newParentData.departmentNumber = parentNode.data;
            break;
          case 3:
            newParentData.sectionNumber = parentNode.data;
            break;
        }

        const result = this.getParentData(node, parentNode.children, newParentData);
        if (result) {
          return result;
        }
      }
    }
    return null; // If the node is not found
  }

  getTypeByLevel(level: number): number {
    switch (level) {
      case 0:
        return 1; // Company
      case 1:
        return 2; // Factory
      case 2:
        return 3; // Department
      case 3:
        return 4; // Section
      default:
        return -1;
    }
  }

  getNodeLevel(node: TreeNode, currentTree: TreeNode[], currentLevel: number = 0): number {
    for (const parentNode of currentTree) {
      if (parentNode === node) {
        return currentLevel;
      }
      if (parentNode.children) {
        const childLevel = this.getNodeLevel(node, parentNode.children, currentLevel + 1);
        if (childLevel !== -1) {
          return childLevel;
        }
      }
    }
    return -1; // Node not found in the current tree
  }

  toggleAllSelection(event: any): void {
    const checked = event.target.checked;
    this.isAllSelected = checked;
    this.selectedNodes = []; // Reset the selection

    if (checked) {
      this.selectAllLeaves(this.treeData); // Select all leaves
    }
  }

  selectAllLeaves(treeNodes: TreeNode[]): void {
    treeNodes.forEach((node) => {
      if (!node.children || node.children.length === 0) {
        // If it's a leaf node, add it to selectedNodes
        this.selectedNodes.push(node);
      } else {
        // Recursively select leaves in child nodes
        this.selectAllLeaves(node.children);
      }
    });
  }

  cancel(): void {
    this.dialogRef.close();
  }
}
